import { useTranslate } from '@tolgee/react';
import canvaIcon from '../../../../assets/images/creator/canva.png';
import dropboxIcon from '../../../../assets/images/creator/dropbox.png';
import onedriveIcon from '../../../../assets/images/creator/onedrive.png';
import googleDriveIcon from '../../../../assets/images/creator/google-drive.png';
import { useClickAway } from 'react-use';
import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import toast from 'react-hot-toast';
import {
  getCloudPickerErrorTranslationKey,
  launchCloudPickerProvider,
  preloadCloudPickerProvider
} from './cloudPickerProviders';
import type {
  CloudPickerFile,
  CloudPickerProvider
} from './cloudPickerProviders';

interface OtherOptionsDropdownProps {
  onCloudFilesPicked: (files: CloudPickerFile[]) => Promise<void> | void;
  disabled?: boolean;
  allowVideoAndPdf?: boolean;
}

const CLOUD_PROVIDER_BUTTONS: Array<{
  provider: CloudPickerProvider;
  iconSrc: string;
  iconAlt: string;
  titleKey: string;
}> = [
  {
    provider: 'dropbox',
    iconSrc: dropboxIcon,
    iconAlt: 'Dropbox',
    titleKey: 'lomavisCreator.additionalOptions.dropboxTitle'
  },
  {
    provider: 'onedrive',
    iconSrc: onedriveIcon,
    iconAlt: 'OneDrive',
    titleKey: 'lomavisCreator.additionalOptions.onedriveTitle'
  }
  // Google Drive button hidden until Google verifies our OAuth client for
  // the `drive.readonly` restricted scope. Uncomment to re-enable — nothing
  // else needs to change (preload + picker code stay active).
  // ,
  // {
  //   provider: 'google_drive',
  //   iconSrc: googleDriveIcon,
  //   iconAlt: 'Google Drive',
  //   titleKey: 'lomavisCreator.additionalOptions.googleDriveTitle'
  // }
];

const OtherOptionsDropdown = ({
  onCloudFilesPicked,
  disabled = false,
  allowVideoAndPdf = true
}: OtherOptionsDropdownProps) => {
  const { t } = useTranslate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeProvider, setActiveProvider] =
    useState<CloudPickerProvider | null>(null);
  const [authedProviders, setAuthedProviders] = useState<
    Record<CloudPickerProvider, boolean>
  >({ google_drive: false, dropbox: false, onedrive: false });
  const [openSubmenuProvider, setOpenSubmenuProvider] =
    useState<CloudPickerProvider | null>(null);
  const ref = useRef(null);

  useEffect(() => {
    preloadCloudPickerProvider('dropbox').catch((error) => {
      console.error(error);
    });
    preloadCloudPickerProvider('google_drive').catch((error) => {
      console.error(error);
    });
  }, []);

  useClickAway(ref, () => {
    if (showDropdown) {
      setShowDropdown(false);
      setOpenSubmenuProvider(null);
    }
  });

  const handleCloudProviderPick = async (
    provider: CloudPickerProvider,
    { forceAccountSelect = false }: { forceAccountSelect?: boolean } = {}
  ) => {
    if (disabled || activeProvider) {
      return;
    }

    try {
      setActiveProvider(provider);
      const pickedFiles = await launchCloudPickerProvider(provider, {
        allowVideoAndPdf,
        locale: navigator.language,
        forceAccountSelect
      });

      setAuthedProviders((prev) => ({ ...prev, [provider]: true }));

      if (pickedFiles.length > 0) {
        setShowDropdown(false);
        await Promise.resolve(onCloudFilesPicked(pickedFiles));
      }
    } catch (error) {
      console.error(`${provider} picker error:`, error);
      toast.error(t(getCloudPickerErrorTranslationKey(provider, error)));
    } finally {
      setActiveProvider(null);
    }
  };

  const handleSwitchAccount = (provider: CloudPickerProvider) => {
    setOpenSubmenuProvider(null);

    if (disabled || activeProvider) {
      return;
    }

    if (provider === 'dropbox') {
      window.open(
        'https://www.dropbox.com/logout',
        '_blank',
        'noopener,noreferrer'
      );
      toast(
        t('lomavisCreator.additionalOptions.dropboxSignOutInstructions'),
        { duration: 6000 }
      );
      return;
    }

    void handleCloudProviderPick(provider, { forceAccountSelect: true });
  };

  const handleCanvaClick = () => {
    if (disabled || activeProvider) {
      return;
    }
    window.open(
      t('lomavisCreator.additionalOptions.canvaLink'),
      '_blank',
      'noopener,noreferrer'
    );
    setShowDropdown(false);
  };

  return (
    <div
      className="d-flex flex-row align-items-start justify-content-end gap-2 flex-wrap"
      ref={ref}
    >
      <Dropdown show={showDropdown}>
        <Dropdown.Toggle
          variant="white"
          size="sm"
          className="pt-1 px-2"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled}
        >
          <i className="fa-regular fa-circle-plus"></i>{' '}
          <span className="text-muted fw-bolder">
            {t('lomavisCreator.otherOptions')}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="p-2" style={{ minWidth: '280px' }}>
          <button
            type="button"
            className="w-100 border-0 bg-transparent d-flex align-items-center gap-2 px-2 py-2 text-start rounded"
            onClick={handleCanvaClick}
            disabled={disabled || activeProvider !== null}
          >
            <img
              src={canvaIcon}
              alt="Canva"
              className="rounded"
              style={{ width: '28px', height: '28px' }}
            />
            <span className="fw-bold text-dark">
              {t('lomavisCreator.additionalOptions.canvaTitle')}
            </span>
            <i className="fa-regular fa-arrow-up-right-from-square ms-auto text-muted"></i>
          </button>

          {CLOUD_PROVIDER_BUTTONS.map(({ provider, iconSrc, iconAlt, titleKey }) => (
            <div
              key={provider}
              className="d-flex align-items-center position-relative"
            >
              <button
                type="button"
                className="flex-grow-1 border-0 bg-transparent d-flex align-items-center gap-2 px-2 py-2 text-start rounded"
                onClick={() => {
                  setOpenSubmenuProvider(null);
                  void handleCloudProviderPick(provider);
                }}
                disabled={disabled || activeProvider !== null}
              >
                <img
                  src={iconSrc}
                  alt={iconAlt}
                  className="rounded"
                  style={{
                    width: '28px',
                    height: '28px',
                    objectFit: 'contain'
                  }}
                />
                <span className="fw-bold text-dark">{t(titleKey)}</span>
                {activeProvider === provider ? (
                  <span
                    className="spinner-border spinner-border-sm ms-auto"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : !authedProviders[provider] ? (
                  <i className="fa-regular fa-chevron-right ms-auto text-muted"></i>
                ) : null}
              </button>
              {authedProviders[provider] && (
                <button
                  type="button"
                  className="border-0 bg-transparent text-muted px-2 py-2 rounded"
                  aria-label={t(
                    'lomavisCreator.additionalOptions.moreOptions'
                  )}
                  aria-haspopup="menu"
                  aria-expanded={openSubmenuProvider === provider}
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenSubmenuProvider((prev) =>
                      prev === provider ? null : provider
                    );
                  }}
                  disabled={disabled || activeProvider !== null}
                >
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </button>
              )}
              {openSubmenuProvider === provider && (
                <div
                  role="menu"
                  className="position-absolute bg-white shadow rounded py-1"
                  style={{
                    top: '100%',
                    right: 0,
                    zIndex: 1050,
                    minWidth: '180px'
                  }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="w-100 border-0 bg-transparent text-start px-3 py-2 small text-dark"
                    onClick={() => handleSwitchAccount(provider)}
                    disabled={disabled || activeProvider !== null}
                  >
                    {t('lomavisCreator.additionalOptions.switchAccount')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default OtherOptionsDropdown;
